import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { api, formatPrice } from '@/lib/api';
import { Trash2, Loader2, ShoppingCart, CreditCard, Apple, Banknote, Truck, Lock } from 'lucide-react';
import { toast } from 'sonner';

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC',
];

export default function Cart() {
    const { items, remove, updateQty, totalAmount } = useCart();
    const { user } = useAuth();
    const [busy, setBusy] = useState(false);
    const nav = useNavigate();

    const [shipping, setShipping] = useState({
        email: user?.email || '',
        name: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
        phone: '',
    });
    const [method, setMethod] = useState('standard');
    const [options, setOptions] = useState([]);
    const [touched, setTouched] = useState({});

    useEffect(() => {
        api.get('/shipping/options').then(({ data }) => setOptions(data.options || []));
    }, []);

    useEffect(() => {
        if (user?.email) setShipping((s) => ({ ...s, email: s.email || user.email }));
    }, [user]);

    const shippingOpt = useMemo(() => options.find((o) => o.key === method) || null, [options, method]);
    const shippingCost = useMemo(() => {
        if (!shippingOpt) return 0;
        if (shippingOpt.free_over != null && totalAmount >= shippingOpt.free_over) return 0;
        return Number(shippingOpt.price || 0);
    }, [shippingOpt, totalAmount]);
    const grandTotal = useMemo(() => Number((totalAmount + shippingCost).toFixed(2)), [totalAmount, shippingCost]);

    const errors = useMemo(() => {
        const e = {};
        if (!shipping.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(shipping.email)) e.email = 'Valid email required';
        if (!shipping.name || shipping.name.length < 2) e.name = 'Full name required';
        if (!shipping.address || shipping.address.length < 4) e.address = 'Street address required';
        if (!shipping.city || shipping.city.length < 2) e.city = 'City required';
        if (!shipping.state) e.state = 'State required';
        if (!shipping.zip || shipping.zip.length < 3) e.zip = 'ZIP required';
        return e;
    }, [shipping]);
    const isValid = Object.keys(errors).length === 0;

    const setField = (k, v) => setShipping((s) => ({ ...s, [k]: v }));

    const checkout = async () => {
        setTouched({ email: 1, name: 1, address: 1, city: 1, state: 1, zip: 1 });
        if (items.length === 0) return;
        if (!isValid) {
            toast.error('Please fill out the shipping address completely');
            return;
        }
        setBusy(true);
        try {
            const payload = {
                items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
                origin_url: window.location.origin,
                customer_email: shipping.email,
                shipping_name: shipping.name,
                shipping_address: shipping.address,
                shipping_city: shipping.city,
                shipping_state: shipping.state,
                shipping_zip: shipping.zip,
                shipping_country: shipping.country || 'US',
                shipping_phone: shipping.phone || null,
                shipping_method: method,
            };
            const { data } = await api.post('/checkout/session', payload);
            if (data?.url) {
                window.location.href = data.url;
            } else {
                toast.error('Could not create checkout');
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Checkout failed');
        } finally {
            setBusy(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <h1 className="font-display text-4xl text-white mb-6">YOUR CART</h1>
                <div className="rrm-panel p-10 text-center">
                    <ShoppingCart className="w-8 h-8 mx-auto text-white/40" />
                    <div className="mt-3 text-white/70">Cart is empty.</div>
                    <Link to="/shop" className="inline-block mt-4 text-amber-300">Browse parts</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <h1 className="font-display text-4xl text-white mb-6">CHECKOUT</h1>

            <div className="grid lg:grid-cols-[1fr_400px] gap-6">
                <div className="space-y-6">
                    {/* CART ITEMS */}
                    <section className="rrm-panel">
                        <header className="px-5 py-3 border-b border-white/10 font-display text-lg text-white">CART ({items.length})</header>
                        <div className="divide-y divide-white/5">
                            {items.map((it) => (
                                <div key={it.product_id} className="p-4 flex items-center gap-4">
                                    <img src={it.image_url} alt={it.name} className="w-20 h-20 rounded-md object-cover bg-black/40" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white font-medium line-clamp-1">{it.name}</div>
                                        <div className="text-xs text-white/50 font-mono">{it.sku}</div>
                                        <div className="text-sm text-amber-400 mt-1">{formatPrice(it.price)}</div>
                                    </div>
                                    <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-md">
                                        <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-2 py-1 text-white/70" data-testid="cart-qty-minus">−</button>
                                        <div className="px-3 text-sm">{it.quantity}</div>
                                        <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-2 py-1 text-white/70" data-testid="cart-qty-plus">+</button>
                                    </div>
                                    <div className="w-24 text-right font-mono text-white/80">{formatPrice(it.price * it.quantity)}</div>
                                    <button onClick={() => remove(it.product_id)} className="p-2 text-white/60 hover:text-rose-400" data-testid="cart-remove">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* SHIPPING ADDRESS */}
                    <section className="rrm-panel" data-testid="checkout-shipping-section">
                        <header className="px-5 py-3 border-b border-white/10 font-display text-lg text-white inline-flex items-center gap-2">
                            <Truck className="w-4 h-4 text-amber-400" /> SHIPPING ADDRESS
                            <span className="ml-2 text-[10px] tracking-widest text-rose-300 bg-rose-500/10 border border-rose-500/30 px-2 py-0.5 rounded-full">REQUIRED</span>
                        </header>
                        <div className="p-5 grid sm:grid-cols-2 gap-3">
                            <Field label="Email" required testid="checkout-email-input" value={shipping.email} onChange={(v) => setField('email', v)} onBlur={() => setTouched({ ...touched, email: 1 })} error={touched.email && errors.email} type="email" />
                            <Field label="Full name" required testid="checkout-name-input" value={shipping.name} onChange={(v) => setField('name', v)} onBlur={() => setTouched({ ...touched, name: 1 })} error={touched.name && errors.name} />
                            <div className="sm:col-span-2">
                                <Field label="Street address" required testid="checkout-address-input" value={shipping.address} onChange={(v) => setField('address', v)} onBlur={() => setTouched({ ...touched, address: 1 })} error={touched.address && errors.address} />
                            </div>
                            <Field label="City" required testid="checkout-city-input" value={shipping.city} onChange={(v) => setField('city', v)} onBlur={() => setTouched({ ...touched, city: 1 })} error={touched.city && errors.city} />
                            <div>
                                <Label>State</Label>
                                <select
                                    value={shipping.state}
                                    onChange={(e) => { setField('state', e.target.value); setTouched({ ...touched, state: 1 }); }}
                                    data-testid="checkout-state-input"
                                    className={`w-full bg-white/5 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 ${touched.state && errors.state ? 'border-rose-500/50' : 'border-white/10'}`}
                                >
                                    <option value="">Select state</option>
                                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                                {touched.state && errors.state ? <FieldError>{errors.state}</FieldError> : null}
                            </div>
                            <Field label="ZIP code" required testid="checkout-zip-input" value={shipping.zip} onChange={(v) => setField('zip', v)} onBlur={() => setTouched({ ...touched, zip: 1 })} error={touched.zip && errors.zip} />
                            <Field label="Phone (optional)" testid="checkout-phone-input" value={shipping.phone} onChange={(v) => setField('phone', v)} />
                        </div>
                    </section>

                    {/* SHIPPING METHOD */}
                    <section className="rrm-panel" data-testid="checkout-shipping-method-section">
                        <header className="px-5 py-3 border-b border-white/10 font-display text-lg text-white inline-flex items-center gap-2">
                            <Truck className="w-4 h-4 text-amber-400" /> SHIPPING METHOD
                        </header>
                        <div className="p-5 space-y-2">
                            {options.map((o) => {
                                const isFree = o.free_over != null && totalAmount >= o.free_over;
                                const eff = isFree ? 0 : o.price;
                                return (
                                    <label key={o.key} className={`flex items-center justify-between gap-3 p-3 rounded-md border cursor-pointer ${method === o.key ? 'border-amber-400/60 bg-amber-500/5' : 'border-white/10 hover:border-white/20'}`}>
                                        <div className="flex items-center gap-3">
                                            <input type="radio" name="ship" checked={method === o.key} onChange={() => setMethod(o.key)} data-testid={`checkout-shipping-${o.key}`} className="accent-amber-500" />
                                            <div>
                                                <div className="text-sm text-white">{o.label}</div>
                                                {o.free_over != null && (
                                                    <div className="text-[10px] text-emerald-300/80 tracking-widest">
                                                        FREE over {formatPrice(o.free_over)}
                                                        {totalAmount < o.free_over && (
                                                            <span className="text-white/40"> · add {formatPrice(o.free_over - totalAmount)} more for free shipping</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className={`font-mono text-sm ${isFree ? 'text-emerald-300' : 'text-white/80'}`}>
                                            {isFree ? 'FREE' : formatPrice(o.price)}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </section>
                </div>

                {/* SUMMARY */}
                <aside className="rrm-panel p-5 h-fit space-y-4 lg:sticky lg:top-20" data-testid="checkout-order-summary">
                    <div className="font-display text-2xl text-white">ORDER SUMMARY</div>
                    <div className="flex items-center justify-between text-sm text-white/70">
                        <span>Subtotal ({items.length} item{items.length > 1 ? 's' : ''})</span>
                        <span className="font-mono" data-testid="checkout-subtotal">{formatPrice(totalAmount)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-white/70">
                        <span>Shipping {shippingOpt ? `· ${shippingOpt.label.split(' (')[0]}` : ''}</span>
                        <span className={`font-mono ${shippingCost === 0 ? 'text-emerald-300' : ''}`} data-testid="checkout-shipping-cost">
                            {shippingCost === 0 ? 'FREE' : formatPrice(shippingCost)}
                        </span>
                    </div>
                    <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                        <span className="text-white/70">Total</span>
                        <span className="font-display text-2xl text-amber-400" data-testid="checkout-total">{formatPrice(grandTotal)}</span>
                    </div>
                    <button
                        disabled={busy || !isValid}
                        onClick={checkout}
                        data-testid="checkout-submit-button"
                        className="w-full px-4 py-3 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                        Pay {formatPrice(grandTotal)}
                    </button>
                    {!isValid && (
                        <div className="text-xs text-amber-300 text-center" data-testid="checkout-validation-message">
                            Complete the shipping address to continue
                        </div>
                    )}
                    <div className="text-[10px] text-white/40 text-center inline-flex items-center justify-center gap-3 flex-wrap pt-2">
                        <span className="inline-flex items-center gap-1"><Apple className="w-3 h-3" /> Apple Pay</span>
                        <span className="inline-flex items-center gap-1"><Banknote className="w-3 h-3" /> Cash App</span>
                        <span>· Cards</span>
                        <span>· Stripe Secure Checkout</span>
                    </div>
                </aside>
            </div>
        </div>
    );
}

function Label({ children }) {
    return <div className="text-xs uppercase tracking-widest text-white/50 mb-2">{children}</div>;
}
function FieldError({ children }) {
    return <div className="text-[11px] text-rose-300 mt-1">{children}</div>;
}
function Field({ label, value, onChange, onBlur, required, testid, error, type = 'text' }) {
    return (
        <div>
            <Label>
                {label} {required && <span className="text-rose-300 ml-0.5">*</span>}
            </Label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                data-testid={testid}
                className={`w-full bg-white/5 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 ${error ? 'border-rose-500/50' : 'border-white/10'}`}
            />
            {error ? <FieldError>{error}</FieldError> : null}
        </div>
    );
}
